/* TD UX-CONTENT 11.10.2026 — design ideas #9 (FAQ live filter + topic chips), #14 (calmer Judge.me review cards),
   #16 (customer video posters, native controls after the tap, German captions + transcript dialog).
   Loaded with defer from sections/td-faq.liquid, sections/apps.liquid and sections/td-video-reviews.liquid; may be included
   more than once per page, so it guards itself.
   Codex R1 (11.10.2026): the transcript dialog ignores answers that arrive after it was closed or reopened for another
   video (request generation); every listener, observer and caption blob URL is registered per owner element and released
   on shopify:section:unload (theme editor), so a reloaded section starts clean. */
(() => {
  if (window.__tdUxContent) return;
  window.__tdUxContent = true;

  const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- cleanup registry (Codex R1)
     owner element → cleanup functions; released when the owner's section unloads (or the owner is set up again) */
  const registry = new Map();
  const onCleanup = (owner, fn) => {
    let list = registry.get(owner);
    if (!list) registry.set(owner, (list = []));
    list.push(fn);
  };
  const release = (owner) => {
    const list = registry.get(owner);
    if (!list) return;
    registry.delete(owner);
    list.forEach((fn) => { try { fn(); } catch (err) { /* ignore */ } });
  };
  const releaseWithin = (root) => {
    Array.from(registry.keys()).forEach((owner) => {
      if (owner === root || root.contains(owner)) release(owner);
    });
  };
  /* one AbortController per owner: every addEventListener of that owner passes its signal */
  const signalFor = (owner) => {
    const ctrl = new AbortController();
    onCleanup(owner, () => ctrl.abort());
    return ctrl.signal;
  };

  /* ---------------------------------------------------------------- #9 FAQ */
  const fold = (s) => (s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/ß/g, 'ss')
    .replace(/\s+/g, ' ')
    .trim();

  // light stemming so "waschen" also finds "waschbar" / "Waschmaschine", "washing" finds "wash"
  const stem = (w) => {
    const m = w.match(/^(.{4,}?)(ing|en|er|es|e|n|s)$/);
    return m ? m[1] : w;
  };

  function initFaq(topics) {
    if (topics.dataset.tdReady) return;
    topics.dataset.tdReady = '1';
    const signal = signalFor(topics);
    onCleanup(topics, () => {
      delete topics.dataset.tdReady;
      if (window.CSS && CSS.highlights) CSS.highlights.delete('td-faq-hit');
    });
    const section = topics.closest('.shopify-section--faq') || topics.closest('section') || document;
    const content = section.querySelector('.faq__content');
    if (!content) return;

    const input = topics.querySelector('[data-td-faq-input]') ||
      document.querySelector('[data-td-support-search] [data-td-faq-input]');
    const searchRoot = input ? (input.closest('[data-td-faq-topics]') || input.closest('[data-td-support-search]')) : null;
    const status = searchRoot ? searchRoot.querySelector('[data-td-faq-status]') : null;
    const none = searchRoot ? searchRoot.querySelector('[data-td-faq-none]') : null;
    const supportRoot = document.querySelector('[data-td-support-search]');
    const minLen = Math.max(1, parseInt((supportRoot && supportRoot.dataset.minQueryLength) || '2', 10) || 2);
    const one = topics.dataset.tdFaqOne || '1';
    const many = topics.dataset.tdFaqMany || '[n]';

    const cats = [];
    const items = [];
    let cat = null;
    Array.from(content.children).forEach((el) => {
      if (el.classList.contains('faq__category')) {
        cat = { el, id: el.id, items: [] };
        cats.push(cat);
        return;
      }
      if (!el.classList.contains('accordion')) return;
      const toggle = el.querySelector('.accordion__toggle');
      const body = el.querySelector('.accordion__content');
      const item = {
        el,
        toggle,
        text: fold((toggle ? toggle.textContent : '') + ' ' + (body ? body.textContent : '')),
        hit: true
      };
      items.push(item);
      if (cat) cat.items.push(item);
    });

    const chips = new Map();
    topics.querySelectorAll('[data-td-faq-chip]').forEach((chip) => chips.set(chip.dataset.tdFaqChip, chip));
    const tocLinks = Array.from(section.querySelectorAll('.faq__toc-item'));
    let active = false;

    function highlight(rawWords) {
      if (!window.CSS || !CSS.highlights || typeof Highlight === 'undefined') return;
      const ranges = [];
      if (rawWords.length) {
        items.forEach((item) => {
          if (!item.hit || !item.toggle) return;
          const walker = document.createTreeWalker(item.toggle, NodeFilter.SHOW_TEXT);
          let node;
          while ((node = walker.nextNode())) {
            const low = node.data.toLowerCase();
            rawWords.forEach((w) => {
              let i = low.indexOf(w);
              while (i > -1) {
                const r = new Range();
                r.setStart(node, i);
                r.setEnd(node, i + w.length);
                ranges.push(r);
                i = low.indexOf(w, i + w.length);
              }
            });
          }
        });
      }
      if (ranges.length) CSS.highlights.set('td-faq-hit', new Highlight(...ranges));
      else CSS.highlights.delete('td-faq-hit');
    }

    function apply(raw) {
      const q = fold(raw);
      const words = q.length >= minLen ? q.split(' ').filter(Boolean) : [];
      active = words.length > 0;
      let total = 0;
      items.forEach((item) => {
        item.hit = !active || words.every((w) => item.text.indexOf(w) > -1 || (stem(w) !== w && item.text.indexOf(stem(w)) > -1));
        item.el.classList.toggle('td-faq-hidden', !item.hit);
        if (item.hit) total += 1;
      });
      cats.forEach((c) => {
        const n = c.items.filter((i) => i.hit).length;
        c.el.classList.toggle('td-faq-hidden', active && n === 0);
        const chip = chips.get(c.id);
        if (chip) {
          const count = chip.querySelector('[data-td-faq-chip-count]');
          const shown = active ? n : parseInt(count ? count.dataset.total : '0', 10) || 0;
          if (count) count.textContent = String(shown);
          const unit = chip.querySelector('[data-td-faq-chip-unit]');
          if (unit) unit.textContent = ' ' + (shown === 1 ? unit.dataset.one : unit.dataset.many);
          chip.classList.toggle('is-empty', active && n === 0);
        }
      });
      tocLinks.forEach((a) => {
        const c = cats.find((x) => '#' + x.id === a.getAttribute('href'));
        a.classList.toggle('td-faq-hidden', !!(active && c && !c.items.some((i) => i.hit)));
      });
      if (status) {
        status.textContent = active ? (total === 1 ? one : many.replace('[n]', String(total))) : '';
        status.classList.toggle('is-zero', active && total === 0);
      }
      if (none) none.hidden = !(active && total === 0);
      highlight(active ? raw.toLowerCase().trim().split(/\s+/).filter((w) => w.length >= minLen) : []);
    }

    if (input) {
      input.addEventListener('input', () => apply(input.value), { signal });
      input.addEventListener('search', () => apply(input.value), { signal });
      if (input.value) apply(input.value);
    }

    topics.addEventListener('click', (event) => {
      const chip = event.target.closest('[data-td-faq-chip]');
      if (!chip) return;
      const c = cats.find((x) => x.id === chip.dataset.tdFaqChip);
      if (!c) return;
      event.preventDefault();
      if (active && !c.items.some((i) => i.hit) && input) {
        input.value = '';
        apply('');
      }
      c.el.setAttribute('tabindex', '-1');
      c.el.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      c.el.focus({ preventScroll: true });
      if (history.replaceState) history.replaceState(null, '', '#' + c.id);
    }, { signal });

    const contact = topics.querySelector('[data-td-faq-contact]');
    if (contact) {
      const target = document.querySelector(contact.getAttribute('href'));
      if (!target) contact.closest('li').hidden = true;
    }
  }

  /* ---------------------------------------------------------------- #14 Judge.me review cards */
  const foldJm = (s) => (s || '').toLowerCase().replace(/\s+/g, ' ').trim();

  function tidyReview(item) {
    const key = item.getAttribute('review-uuid') || '1';
    if (item.dataset.tdJm === key) return;
    item.dataset.tdJm = key;

    const title = item.querySelector('.jm-review-content__title');
    const body = item.querySelector('.jm-review-content__body');
    if (title && body) {
      const t = foldJm(title.textContent).replace(/(\.\.\.|…)$/, '').replace(/[\s.,;:!?]+$/, '');
      const b = foldJm(body.textContent);
      const wrap = title.closest('.jm-review-content__title-wrapper') || title;
      // the title only repeats the beginning of the review text → hide it
      const dup = t.length > 0 && b.indexOf(t) === 0 && (b.length === t.length || /[\s.,;:!?…)"'-]/.test(b.charAt(t.length)));
      wrap.classList.toggle('td-jm-dup', dup);
    }

    const votes = item.querySelector('.jm-voting-controls');
    if (votes) {
      const counts = Array.from(votes.querySelectorAll('button')).map((btn) => parseInt((btn.textContent || '').replace(/\D/g, ''), 10) || 0);
      let row = votes;
      while (row.parentElement && row.parentElement !== item && !row.parentElement.classList.contains('jm-review-item')) {
        if (row.parentElement.querySelector('.jm-review-content')) break;
        row = row.parentElement;
      }
      row.classList.toggle('td-jm-novotes', counts.length > 0 && counts.every((n) => n === 0));
    }
  }

  function tidyAll(root) {
    root.querySelectorAll('.jm-review-item').forEach(tidyReview);
  }

  function initReviews() {
    const attach = (root) => {
      if (registry.has(root)) return; /* already observed */
      tidyAll(root);
      const mo = new MutationObserver(() => tidyAll(root));
      mo.observe(root, { childList: true, subtree: true });
      onCleanup(root, () => mo.disconnect());
    };
    const root = document.getElementById('judgeme_product_reviews');
    if (root) {
      attach(root);
      return;
    }
    const apps = document.querySelector('.shopify-section--apps');
    if (!apps || registry.has(apps)) return;
    const waiter = new MutationObserver(() => {
      const r = document.getElementById('judgeme_product_reviews');
      if (r) {
        release(apps);
        attach(r);
      }
    });
    waiter.observe(document.body, { childList: true, subtree: true });
    const timer = setTimeout(() => release(apps), 20000);
    onCleanup(apps, () => { waiter.disconnect(); clearTimeout(timer); });
  }

  /* ---------------------------------------------------------------- #16 customer videos */
  const vttCache = new Map();

  function loadVtt(url) {
    if (!vttCache.has(url)) {
      vttCache.set(url, fetch(url, { credentials: 'same-origin' }).then((r) => {
        if (!r.ok) throw new Error('vtt ' + r.status);
        return r.text();
      }));
    }
    return vttCache.get(url);
  }

  function vttToText(vtt) {
    const out = [];
    vtt.replace(/\r/g, '').split(/\n\n+/).forEach((block) => {
      const lines = block.split('\n');
      const i = lines.findIndex((l) => l.indexOf('-->') > -1);
      if (i < 0) return;
      const t = lines.slice(i + 1).join(' ').replace(/<[^>]+>/g, '').trim();
      if (t) out.push(t);
    });
    return out.join(' ').replace(/\s+/g, ' ').trim();
  }

  function toParagraphs(text) {
    const sentences = text.match(/[^.!?]+[.!?]+["“”»]?\s*|[^.!?]+$/g) || [text];
    const paras = [];
    for (let i = 0; i < sentences.length; i += 3) paras.push(sentences.slice(i, i + 3).join('').trim());
    return paras;
  }

  // Shopify serves .vtt theme assets as application/octet-stream; some browsers refuse such tracks.
  // Re-attach each caption track from a text/vtt blob so captions work everywhere.
  // The blob URL belongs to the section (owner) and is revoked when it unloads; a section that is gone by the time the
  // file arrives gets no blob at all.
  function prepareTrack(video, owner) {
    const track = video.querySelector('track[data-td-vtt]');
    if (!track || track.dataset.tdBlob) return;
    track.dataset.tdBlob = 'pending';
    loadVtt(track.getAttribute('src')).then((text) => {
      if (!video.isConnected || !track.isConnected || !registry.has(owner)) { track.dataset.tdBlob = ''; return; }
      const url = URL.createObjectURL(new Blob([text], { type: 'text/vtt' }));
      onCleanup(owner, () => URL.revokeObjectURL(url));
      const fresh = document.createElement('track');
      fresh.kind = 'captions';
      fresh.srclang = track.srclang || 'de';
      fresh.label = track.label;
      fresh.src = url;
      fresh.dataset.tdVtt = '';
      fresh.dataset.tdBlob = '1';
      fresh.dataset.tdDefault = track.default ? '1' : '';
      const wantShowing = track.default;
      track.remove();
      video.appendChild(fresh);
      if (fresh.track) fresh.track.mode = wantShowing ? 'showing' : 'hidden';
    }).catch(() => { track.dataset.tdBlob = ''; });
  }

  function initVideoSection(section) {
    if (section.dataset.tdVrReady) return;
    section.dataset.tdVrReady = '1';
    const medias = Array.from(section.querySelectorAll('[data-td-vr]'));
    if (!medias.length && !section.querySelector('[data-td-vr-transcript]')) return;
    const signal = signalFor(section);
    onCleanup(section, () => { delete section.dataset.tdVrReady; });

    const pauseOthers = (current) => {
      section.querySelectorAll('video').forEach((v) => { if (v !== current && !v.paused) v.pause(); });
    };

    medias.forEach((media) => {
      const video = media.querySelector('video');
      const btn = media.querySelector('[data-td-vr-play]');
      if (!video || !btn) return;
      let down = null;
      btn.addEventListener('mousedown', (e) => { down = [e.clientX, e.clientY]; }, { signal });
      btn.addEventListener('click', (e) => {
        // ignore the click that ends a mouse drag of the carousel
        if (down && e.detail > 0 && Math.hypot(e.clientX - down[0], e.clientY - down[1]) > 6) { down = null; return; }
        down = null;
        prepareTrack(video, section);
        media.classList.add('is-playing');
        video.controls = true;
        pauseOthers(video);
        const p = video.play();
        if (p && p.catch) p.catch(() => {});
        try { video.focus({ preventScroll: true }); } catch (err) { /* ignore */ }
      }, { signal });
      video.addEventListener('play', () => pauseOthers(video), { signal });
      video.addEventListener('ended', () => {
        const hadFocus = document.activeElement === video;
        video.controls = false;
        media.classList.remove('is-playing');
        try { video.currentTime = 0; } catch (err) { /* ignore */ }
        if (hadFocus) btn.focus({ preventScroll: true });
      }, { signal });
    });

    // fetch the caption files once the section comes near the viewport
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        if (entries.some((en) => en.isIntersecting)) {
          io.disconnect();
          medias.forEach((m) => { const v = m.querySelector('video'); if (v) prepareTrack(v, section); });
        }
      }, { rootMargin: '300px 0px' });
      io.observe(section);
      onCleanup(section, () => io.disconnect());
      // pause a playing video once it is swiped / scrolled out of view
      const vio = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          const v = en.target.querySelector('video');
          if (!en.isIntersecting && v && !v.paused) v.pause();
        });
      }, { threshold: 0.2 });
      medias.forEach((m) => vio.observe(m));
      onCleanup(section, () => vio.disconnect());
    }

    const dialog = section.querySelector('[data-td-vr-dialog]');
    if (!dialog) return;
    const titleEl = dialog.querySelector('[data-td-vr-dialog-title]');
    const textEl = dialog.querySelector('[data-td-vr-dialog-text]');
    let opener = null;
    /* Codex R1 — request generation: each open (and each close) starts a new one; an answer only fills the dialog while
       its generation is current, so video A's text never lands under video B's title (open A, close, open B, B first) */
    let generation = 0;
    const invalidate = () => {
      generation += 1;
      textEl.removeAttribute('aria-busy');
    };

    const close = () => {
      invalidate();
      if (typeof dialog.close === 'function' && dialog.open) dialog.close();
      else dialog.removeAttribute('open');
    };
    dialog.addEventListener('close', () => {
      /* the close event is queued: when the dialog was opened again meanwhile (another video), leave that request alone */
      if (dialog.hasAttribute('open')) return;
      invalidate(); /* Esc closes without close() */
      if (opener && document.contains(opener)) opener.focus({ preventScroll: true });
    }, { signal });
    dialog.querySelector('[data-td-vr-close]').addEventListener('click', close, { signal });
    dialog.addEventListener('click', (e) => { if (e.target === dialog) close(); }, { signal });
    onCleanup(section, invalidate);

    section.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-td-vr-transcript]');
      if (!btn) return;
      opener = btn;
      generation += 1;
      const mine = generation;
      const current = () => mine === generation && dialog.hasAttribute('open');
      titleEl.textContent = btn.dataset.title || '';
      textEl.textContent = '';
      textEl.setAttribute('aria-busy', 'true');
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      loadVtt(btn.dataset.vtt).then((vtt) => {
        if (!current()) return;
        textEl.textContent = '';
        toParagraphs(vttToText(vtt)).forEach((para) => {
          const p = document.createElement('p');
          p.textContent = para;
          textEl.appendChild(p);
        });
      }).catch(() => {
        if (!current()) return;
        textEl.textContent = dialog.dataset.error || '';
      }).finally(() => {
        if (mine === generation) textEl.removeAttribute('aria-busy');
      });
    }, { signal });
  }

  /* ---------------------------------------------------------------- boot */
  function boot() {
    document.querySelectorAll('[data-td-faq-topics]').forEach(initFaq);
    document.querySelectorAll('.shopify-section--td-video-reviews').forEach(initVideoSection);
    initReviews();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();

  document.addEventListener('shopify:section:load', (event) => {
    const s = event.target;
    if (!s || !s.querySelectorAll) return;
    s.querySelectorAll('[data-td-faq-topics]').forEach(initFaq);
    if (s.classList.contains('shopify-section--td-video-reviews')) initVideoSection(s);
    /* the support search (another section) holds the FAQ's search field: set the FAQ up again with the new field */
    if (s.querySelector('[data-td-support-search]')) {
      document.querySelectorAll('[data-td-faq-topics]').forEach((topics) => { release(topics); initFaq(topics); });
    }
    if (s.classList.contains('shopify-section--apps') || s.querySelector('#judgeme_product_reviews')) initReviews();
  });

  /* Codex R1 — theme editor: a section that is removed or re-rendered releases its listeners, observers and blob URLs */
  document.addEventListener('shopify:section:unload', (event) => {
    const s = event.target;
    if (s && s.contains) releaseWithin(s);
  });
})();
